import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@retailer-platform/shared-components'
import { Button, TextArea, Text } from '@retailer-platform/shared-components/src/tds'
import { typography } from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../intl/intl.hooks'

interface CommentModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (comment: string) => void
  initialComment?: string
}

const CommentTextArea = styled(TextArea)({
  width: '100%',
  minHeight: '120px',
  marginBottom: '16px',
})

const CommentPrompt = styled.div({
  marginBottom: '24px',
  ...typography.bodyRegular,
})

export const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialComment = '',
}) => {
  const [commentText, setCommentText] = useState('')

  const intlMessages = useDashMessages({
    addCommentTitle: 'aiAgent.chatMessage.feedback.addCommentTitle',
    addCommentPrompt: 'aiAgent.chatMessage.feedback.addCommentPrompt',
    addCommentPlaceholder: 'aiAgent.chatMessage.feedback.addCommentPlaceholder',
    save: 'aiAgent.chatMessage.feedback.save',
    cancel: 'aiAgent.chatMessage.feedback.cancel',
  })

  // Reset comment text when modal opens/closes or initial comment changes
  useEffect(() => {
    if (isOpen) {
      setCommentText(initialComment)
    } else {
      setCommentText('')
    }
  }, [isOpen, initialComment])

  const handleSave = () => {
    if (commentText.trim()) {
      onSave(commentText.trim())
    }
  }

  const handleClose = () => {
    setCommentText('')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      maxWidth={500}
      maxHeight={400}
      data-testid="comment-modal"
    >
      <ModalHeader>
        <Text typography="title">{intlMessages.addCommentTitle}</Text>
      </ModalHeader>
      <ModalBody>
        <CommentPrompt>{intlMessages.addCommentPrompt}</CommentPrompt>
        <CommentTextArea
          value={commentText}
          onChange={e => setCommentText(e.target.value)}
          placeholder={intlMessages.addCommentPlaceholder}
          data-testid="comment-textarea"
        />
      </ModalBody>
      <ModalFooter>
        <Button.Group>
          <Button variant="secondary" onClick={handleClose} data-testid="cancel-button">
            {intlMessages.cancel}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!commentText.trim()}
            data-testid="save-button"
          >
            {intlMessages.save}
          </Button>
        </Button.Group>
      </ModalFooter>
    </Modal>
  )
}
